import { useCallback, useState } from 'react';
import { addDays, format } from 'date-fns';
import { useSendEmailMutation } from '@/features/msGraph/msGraphApi';
import { buildOutboundEmailSharepointMetadata } from '@/features/msGraph/helper';
import { triggerRefresh } from '@/features/table/attachmentsTableSlice';
import { showToast } from '@/features/toast/toastSlice';
import { useMsGraph } from '@/hooks/useMsGraph';
import { useResolveUnlayerMergeTags } from '@/components/Drawers/AddUnlayerEmailDrawer/helpers/resolveUnlayerMergeTags';
import { replacePlaceholderTagsInHtmlString } from '@/utils/unlayer';
import { convertFilesForUpload } from '@/features/msGraph/helper';
import { useDispatch, useSelector } from 'react-redux';
import { closeDialog } from '@/features/dialog/dialogSlice';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useCreateTaskMutation } from '@/features/engagements/engagementsApi';
import { useAutomationContext } from '@/context/automationContext';
import { AUTOMATION_NAMES } from '@/utils/automationCatalog';

/**
 * Handles the full send flow for the Unlayer compose drawer, including merge-tag
 * resolution, Graph send, optional SharePoint upload, and user feedback toasts.
 */
export const useSendUnlayerEmail = () => {
	const dispatch = useDispatch();
	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;
	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const { getAutomations } = useAutomationContext();
	const saveEmailAttachmentsEnabled = Boolean(
		getAutomations(AUTOMATION_NAMES.saveEmailAttachments)?.enabled,
	);

	const [sendError, setSendError] = useState('');
	const [sendEmail] = useSendEmailMutation();
	const [createTask] = useCreateTaskMutation();
	const { uploadEmail, uploadFilesAndRetry } = useMsGraph();

	const resolveUnlayerMergeTags = useResolveUnlayerMergeTags();

	const clearSendError = useCallback(() => {
		setSendError('');
	}, []);

	const createFollowUpTaskFromEmail = useCallback(
		async ({ values, subject }) => {
			if (!values?.createFollowUpTask) return;

			const dealId = values.deal?.dealId ?? null;
			const clientId = values.client?.clientId ?? null;
			const objectType = dealId ? (values.deal?.objectType ?? 'deal') : 'client';
			const dueDate = values.followUpDueDate ?? addDays(new Date(), 3);
			const timestamp = format(new Date(), 'dd/MM/yyyy HH:mm');

			try {
				await createTask({
					title: subject,
					notes: `follow up task created based on email sent ${timestamp}`,
					dueDate,
					type: 'TODO',
					priority: 'NONE',
					ownerDocId: userDetails?.userId,
					...(dealId && { dealId }),
					...(clientId && { clientId }),
					...(objectType && { objectType }),
				}).unwrap();
			} catch (error) {
				console.error('Error creating follow-up task from Unlayer email', error);
				dispatch(
					showToast({
						message: 'Email sent, but the follow-up task could not be created.',
						error: true,
					}),
				);
			}
		},
		[createTask, dispatch, userDetails?.userId],
	);

	// Upload any attachments included with the email to the selected SharePoint folder
	const uploadAttachments = useCallback(
		async ({ driveId, selectedFolder, files }) => {
			const hasNoAttachments = !Array.isArray(files) || files.length === 0;
			if (hasNoAttachments) {
				return { success: true };
			}

			const { success } = await uploadFilesAndRetry({
				driveId,
				itemId: selectedFolder,
				files,
				silent: true,
			});

			return { success };
		},
		[uploadFilesAndRetry],
	);

	// Handle the full upload to SharePoint flow:
	// - upload the email first,
	// - upload any attachments
	// - refresh the attachments table to show the newly uploaded email and attachments without needing a manual page refresh
	// - show a success toast if all uploads succeeded, or an error toast if any upload failed
	const uploadEmailAndAttachments = useCallback(
		async ({
			messageId,
			sendResult,
			driveId,
			selectedFolder,
			subject,
			files,
			metadata,
			saveAttachments,
		}) => {
			if (!messageId || !driveId || !selectedFolder) {
				return;
			}

			const uploadEmailError = await uploadEmail({
				messageId,
				sendResult,
				driveId,
				selectedFolder,
				subject,
				metadata,
			});

			const toast = {
				message:
					'Email sent successfully, but there was an issue saving to the selected SharePoint folder.',
				error: true,
			};
			if (!uploadEmailError.success) {
				dispatch(showToast(toast));
				return;
			}

			if (!saveAttachments) {
				dispatch(triggerRefresh());
				dispatch(
					showToast({
						message: 'Email sent and saved successfully!',
						autohide: true,
					}),
				);
				return;
			}

			const attachmentUploadResult = await uploadAttachments({
				driveId,
				selectedFolder,
				files,
			});

			// refresh the attachments table to show the newly uploaded email and attachments without needing a manual page refresh
			dispatch(triggerRefresh());

			if (attachmentUploadResult.success) {
				toast.message = 'Email and attachments sent and saved successfully!';
				toast.error = false;
				toast.autohide = true;
			}

			dispatch(showToast(toast));
		},
		[dispatch, uploadAttachments, uploadEmail],
	);

	const handleSubmit = useCallback(
		async (values, { setSubmitting, resetForm }) => {
			console.log('Send unlayer email values: ', values);

			setSendError('');
			try {
				const formatRecipients = (array) =>
					(array ?? []).map((contact) => contact.contactEmail).join(',');

				const { tagData, selectedClientDetails, selectedDealDetails } =
					await resolveUnlayerMergeTags(values);

				const resolvedBody = replacePlaceholderTagsInHtmlString(values.editorHtml, tagData);
				const resolvedSubject = replacePlaceholderTagsInHtmlString(
					values.subject.trim(),
					tagData,
				);
				const resolvedFiles = await convertFilesForUpload(values.files);

				const allFilesForEmail = resolvedFiles.map((item) => item.file);

				const filesToUploadToSharePoint = resolvedFiles
					.filter((item) => !item.isSharepointFile)
					.map((item) => item.file);

				const payload = {
					recipient: formatRecipients(values.to),
					...(values.cc.length > 0 && { cc: formatRecipients(values.cc) }),
					...(values.bcc.length > 0 && { bcc: formatRecipients(values.bcc) }),
					from: values.from?.name,
					subject: resolvedSubject,
					body: resolvedBody,
					...(allFilesForEmail.length > 0 ? { files: allFilesForEmail } : {}),
					email,
				};
				const sendResult = await sendEmail(payload).unwrap();
				const messageId = sendResult?.messageId ?? sendResult;
				const sentAt = new Date();

				await uploadEmailAndAttachments({
					messageId,
					sendResult,
					driveId: values.driveId,
					selectedFolder: values.selectedFolder,
					subject: resolvedSubject,
					files: filesToUploadToSharePoint,
					saveAttachments: saveEmailAttachmentsEnabled,
					metadata: buildOutboundEmailSharepointMetadata({
						sendResult,
						values: {
							...values,
							subject: resolvedSubject,
							client: selectedClientDetails,
							deal: selectedDealDetails,
						},
						userDetails,
					}),
				});

				await createFollowUpTaskFromEmail({
					values,
					subject: resolvedSubject,
				});

				if (messageId) {
					resetForm();
					dispatch(closeDialog('unlayerEmail'));
				}
			} catch (error) {
				console.log('🙅 ~ Error sending Unlayer email', error);
				setSendError(`Error sending email ${error?.message ?? ''}`);
			}
			setSubmitting(false);
		},
		[
			createFollowUpTaskFromEmail,
			dispatch,
			email,
			sendEmail,
			uploadEmailAndAttachments,
			userDetails,
			resolveUnlayerMergeTags,
		],
	);

	return {
		clearSendError,
		handleSubmit,
		sendError,
	};
};
